import { z } from "zod";

/**
 * Rechte je Person und Kunde: je Bereich bearbeiten, nur ansehen oder kein Zugriff. Administratoren dürfen immer alles.
 * Die API prüft jede Anfrage zentral (Bereich aus der Route, Lesen = GET); das Portal blendet passend aus.
 */
export const AREAS = {
  orders: { label: "Aufträge", description: "Aufträge, Adressbuch, CSV-Import", levels: ["none", "view", "edit"] },
  products: { label: "Artikel", description: "Stammdaten, Freigaben, Sets", levels: ["none", "view", "edit"] },
  stock: { label: "Bestand", description: "Bestände, MHD, Reichweite & Nachbestellung", levels: ["none", "view", "edit"] },
  inbounds: { label: "Wareneingänge", description: "Anlieferungen anmelden und verfolgen", levels: ["none", "view", "edit"] },
  returns: { label: "Retouren", description: "Retouren, Klärfälle, Retourenportal", levels: ["none", "view", "edit"] },
  reports: { label: "Auswertungen", description: "Leistung, Retouren-Auswertung", levels: ["none", "view"] },
  costs: { label: "Kosten", description: "Kostenübersicht (falls freigeschaltet)", levels: ["none", "view"] },
  production: { label: "Produktion", description: "Produktionsstücklisten und Produktionsaufträge (falls freigeschaltet)", levels: ["none", "view", "edit"] },
  profit: { label: "Profit Center", description: "Deckungsbeiträge, Einstandspreise, Gebühren (falls freigeschaltet)", levels: ["none", "view", "edit"] },
  settings: { label: "Einstellungen", description: "Shops, Regeln, Lager, Team, Module", levels: ["none", "edit"] },
} as const;
export type Area = keyof typeof AREAS;
export type Level = "none" | "view" | "edit";
export const AREA_KEYS = Object.keys(AREAS) as Area[];
export type Permissions = Record<Area, Level>;

const all = (l: Level): Permissions => Object.fromEntries(AREA_KEYS.map((a) => [a, AREAS[a].levels.includes(l as never) ? l : l === "edit" ? "view" : "none"])) as Permissions;

export const PERMISSION_PRESETS = {
  member: { label: "Mitarbeiter", description: "Tagesgeschäft: Aufträge, Artikel, Bestand, Wareneingänge, Retouren – ohne Einstellungen", permissions: { orders: "edit", products: "edit", stock: "edit", inbounds: "edit", returns: "edit", reports: "view", costs: "none", production: "edit", profit: "none", settings: "none" } },
  readonly: { label: "Nur lesen", description: "Sieht alles Operative, ändert nichts", permissions: { ...all("view"), costs: "none", profit: "none", settings: "none" } },
  returns: { label: "Retouren", description: "Retouren bearbeiten, Aufträge und Artikel ansehen", permissions: { orders: "view", products: "view", stock: "none", inbounds: "none", returns: "edit", reports: "view", costs: "none", production: "none", profit: "none", settings: "none" } },
  warehouse: { label: "Lager & Wareneingang", description: "Bestand und Wareneingänge bearbeiten, Artikel und Aufträge ansehen", permissions: { orders: "view", products: "view", stock: "edit", inbounds: "edit", returns: "view", reports: "view", costs: "none", production: "edit", profit: "none", settings: "none" } },
  finance: { label: "Buchhaltung", description: "Kosten und Auswertungen, Aufträge ansehen", permissions: { orders: "view", products: "none", stock: "none", inbounds: "none", returns: "view", reports: "view", costs: "view", production: "view", profit: "none", settings: "none" } },
} as const satisfies Record<string, { label: string; description: string; permissions: Permissions }>;
export type PermissionPreset = keyof typeof PERMISSION_PRESETS;
export const FULL_PERMISSIONS: Permissions = all("edit");

/** fehlender Bereich (z. B. später neu hinzugekommen) = kein Zugriff – bestehende Auswahl bleibt dabei erhalten */
const level = z.enum(["none", "view", "edit"]).default("none");
export const permissionsSchema = z.object({
  /** gewählte Vorlage (nur Anzeige) oder „custom“ */
  preset: z.string().max(30).optional(),
  areas: z.object(Object.fromEntries(AREA_KEYS.map((a) => [a, level])) as Record<Area, typeof level>),
});
export type StoredPermissions = z.infer<typeof permissionsSchema>;

/** wirksame Rechte: Administratoren/Logiful alles; Mitarbeiter je gespeicherter Auswahl, sonst Vorlage „Mitarbeiter“ */
export function effectivePermissions(role: string, stored?: unknown): Permissions {
  if (role !== "customer_user") return { ...FULL_PERMISSIONS };
  const p = permissionsSchema.safeParse(stored);
  const base = p.success ? p.data.areas : PERMISSION_PRESETS.member.permissions;
  // nur erlaubte Stufen je Bereich (z. B. Kosten höchstens ansehen)
  return Object.fromEntries(AREA_KEYS.map((a) => {
    const l = base[a] ?? "none";
    return [a, AREAS[a].levels.includes(l as never) ? l : l === "edit" ? (AREAS[a].levels.includes("view" as never) ? "view" : "none") : "none"];
  })) as Permissions;
}

export const can = (p: Permissions, area: Area, need: "view" | "edit") => (need === "view" ? p[area] !== "none" : p[area] === "edit" || (p[area] === "view" && !AREAS[area].levels.includes("edit" as never)));

/** passende Vorlage zu Rechten (sonst „custom“) */
export function presetOf(p: Permissions): PermissionPreset | "custom" {
  for (const [k, v] of Object.entries(PERMISSION_PRESETS)) if (AREA_KEYS.every((a) => v.permissions[a] === p[a])) return k as PermissionPreset;
  return "custom";
}

/**
 * Bereich einer Kunden-Route (/api/tenants/:tenantId/…) und benötigte Stufe. open = für alle Personen des Kunden
 * (eigene Benachrichtigungen, Handlungsbedarf – dort wird nach Rechten gefiltert). Konfiguration (Shops, Lager, Regeln …)
 * darf jeder lesen (Auswahllisten), ändern nur mit „Einstellungen“.
 */
export function routeArea(pattern: string, method: string): { area: Area; need: "view" | "edit" } | "open" {
  const rest = pattern.replace(/^\/api\/tenants\/:tenantId\/?/, "");
  const seg = rest.split("/");
  const read = method === "GET" || method === "HEAD";
  const need = read ? "view" : "edit";
  const [a, b, c] = seg;
  if (a === "action-items" || a === "notifications") return "open";
  // eigene Übersicht anpassen darf jede Person; Team-Standard nur mit Einstellungsrecht
  if (a === "dashboard") return b === "team" && !read ? { area: "settings", need: "edit" } : "open";
  if (a === "performance" || (a === "returns" && b === "analytics")) return { area: "reports", need: "view" };
  if (a === "costs") return { area: "costs", need: "view" };
  if (a === "profit") return { area: "profit", need };
  if (a === "production") return { area: "production", need };
  if (a === "audit") return { area: "settings", need: "edit" };
  if (a === "orders" && c === "returns") return { area: "returns", need };
  if (a === "orders" || a === "b2b" || a === "fba" || a === "address-book" || a === "address-check" || a === "import-templates") return { area: "orders", need };
  if (a === "products" && (c === "replenishment" || c === "discontinued")) return { area: "stock", need };
  if (a === "products" || a === "product-rules") return { area: "products", need };
  if (a === "stock") return { area: "stock", need };
  if (a === "settings" && (b === "stock-sync" || b === "best-before" || b === "replenishment")) return { area: "stock", need };
  if (a === "inbounds" || a === "suppliers" || a === "purchase-orders" || a === "purchasing") return { area: "inbounds", need };
  if (a === "returns" || a === "return-clarifications" || a === "returns-portal") return { area: "returns", need };
  // Konfiguration: lesen frei, ändern nur mit Einstellungen
  return read ? "open" : { area: "settings", need: "edit" };
}

/** Bereich eines Handlungsbedarf-Punkts (für die Sichtbarkeit) */
export const areaOfAction = (a: string): Area | null =>
  a === "orders" ? "orders" : a === "products" ? "products" : a === "stock" ? "stock" : a === "inbounds" ? "inbounds" : a === "returns" ? "returns" : null;
